import * as React from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { AdminPageHeader } from "@/components/admin/AdminPageHeader"
import { Field, fieldAria } from "@/components/admin/Field"
import { ErrorSummary, FormActions } from "@/components/admin/FormParts"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import type { AdminFaqFormProps, PageProps } from "@/types/inertia"

type FaqFormData = { question: string; answer: string; topic: string }

export default function AdminFaqForm() {
  const { props } = usePage<PageProps<AdminFaqFormProps>>()
  const { faq, topics, submit_url: submitUrl, submit_method: submitMethod, back_url: backUrl } = props
  const editing = faq.id !== null
  const title = editing ? "Edit a question" : "Add a question"
  const description = editing
    ? `Edit the FAQ “${faq.question}”: its wording, answer and topic.`
    : "Add a question and its answer to the public FAQs page, under one of the five topics."

  const form = useForm<FaqFormData>({ question: faq.question, answer: faq.answer, topic: faq.topic })
  const errors = form.errors as Partial<Record<keyof FaqFormData, string>>

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const options = { preserveState: "errors" as const }
    if (submitMethod === "patch") form.patch(submitUrl, options)
    else form.post(submitUrl, options)
  }

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <AdminPageHeader
          title={title}
          description={
            editing
              ? "Changes show on the FAQs page straight away. Moving it to another topic puts it at the end of that topic."
              : "New questions join the end of their topic; move them up from the list."
          }
          back={{ href: backUrl, label: "All questions" }}
        />

        <form onSubmit={submit} className="max-w-2xl" noValidate>
          <ErrorSummary errors={errors} />

          <div className="mt-8 space-y-6">
            <Field id="faq-topic" label="Topic" error={errors.topic}>
              <Select
                {...fieldAria("faq-topic", { error: errors.topic })}
                className="form-control-large"
                value={form.data.topic}
                onChange={(event) => form.setData("topic", event.target.value)}
                required
              >
                <option value="" disabled>
                  Choose a topic…
                </option>
                {topics.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field id="faq-question" label="Question" error={errors.question}>
              <Input
                {...fieldAria("faq-question", { error: errors.question })}
                className="form-control-large"
                value={form.data.question}
                onChange={(event) => form.setData("question", event.target.value)}
                required
              />
            </Field>

            <Field id="faq-answer" label="Answer" error={errors.answer} hint="Plain text, shown in full when a visitor opens the question.">
              <Textarea
                {...fieldAria("faq-answer", { error: errors.answer, hint: true })}
                className="form-control-large"
                rows={8}
                value={form.data.answer}
                onChange={(event) => form.setData("answer", event.target.value)}
                required
              />
            </Field>
          </div>

          <FormActions processing={form.processing} submitLabel={editing ? "Save changes" : "Add question"} cancelHref={backUrl} />
        </form>
      </AdminShell>
    </>
  )
}
